import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { BarChart, MessageSquare, History, Brain, FileText, Briefcase, Mail, CheckSquare2, Calendar, Users2, Video, Sparkles, ClipboardList } from "lucide-react";
import { CandidateKanban } from "@/components/candidates/CandidateKanban";
import { CandidateComments } from "@/components/candidates/CandidateComments";
import { CandidateHistoryTab } from "@/components/candidates/CandidateHistoryTab";
import { CandidateAnalysisTab } from "@/components/candidates/CandidateAnalysisTab";
import { CandidateProfilesTab } from "./CandidateProfilesTab";
import { CandidateExperienceTab } from "./CandidateExperienceTab";
import { CandidateAssignmentsTab } from "./CandidateAssignmentsTab";
import { CandidateTasksTab } from "./CandidateTasksTab";
import { CandidateCalendarTab } from "./CandidateCalendarTab";
import { CandidateComment, Candidate } from "@/types/candidate";
import { useState, useEffect, useMemo } from "react";
import { toast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";
import { CandidateDocumentsTab } from "./CandidateDocumentsTab";
import { CandidatePotentialMatchesTab } from "./CandidatePotentialMatchesTab";
import { useAuth } from "@/providers/AuthProvider";
import { useFeatureFlag } from "@/providers/FeatureFlagsProvider";
import { EmbeddedTranscripts } from "@/components/transcripts/EmbeddedTranscripts";
import { EntityMailbox } from "@/components/inbox/EntityMailbox";
import { useEntityConversations } from "@/hooks/inbox/useEntityConversations";
import { DynamicTabBar, Tab } from "@/components/ui/DynamicTabBar";
import { CandidateScorecardTab } from "./scorecard/CandidateScorecardTab";
import { useScorecardFields } from "@/hooks/scorecard/useScorecardConfig";

interface CandidateTabsProps {
  candidateId: string;
  comments: CandidateComment[];
  cv: string | null;
  candidate: Candidate;
  handleAddComment: (content: string) => Promise<void>;
  handleUpdateComment: (commentId: string, content: string) => Promise<void>;
  handleDeleteComment: (commentId: string) => Promise<void>;
  activeTab: string;
  setActiveTab: (tab: string) => void;
  refetchCandidate?: () => Promise<void>;
  kanbanRefreshKey?: number;
}

export const CandidateTabs = ({
  candidateId,
  comments,
  cv,
  candidate,
  handleAddComment,
  handleUpdateComment,
  handleDeleteComment,
  activeTab,
  setActiveTab,
  refetchCandidate,
  kanbanRefreshKey
}: CandidateTabsProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();
  const { currentOrganization, user } = useAuth();
  const organizationId = currentOrganization?.id;
  const transcriptsEnabled = useFeatureFlag("transcripts");

  // Warm the mailbox cache (and seed per-thread message caches) while the
  // page loads, so opening the Mailbox tab is instant.
  useEntityConversations("candidate", candidateId);

  // Shared cached fetch: also used inside the Scorecard tab itself.
  const { hasFields: hasScorecardFields } = useScorecardFields(organizationId);

  const hasCandidateAiAnalysis = useMemo(() => {
    if (!candidate.ai_analysis)
      return false;

    if (typeof candidate.ai_analysis !== "object")
      return false;

    return Object.keys(candidate.ai_analysis).length > 0;
  }, [candidate.ai_analysis]);

  const tabConfig: Tab[] = useMemo(() => [
    { id: "processes", label: t("candidates.tabs.recruitmentProcess"), icon: <BarChart className="h-3.5 w-3.5" /> },
    { id: "analysis", label: t("candidates.tabs.aiAnalysis"), icon: <Brain className="h-3.5 w-3.5" /> },
    { id: "experience", label: t("candidates.tabs.experience", "Experience"), icon: <Briefcase className="h-3.5 w-3.5" /> },
    { id: "potential-matches", label: t("candidates.tabs.potentialMatches", "Potential Matches"), icon: <Sparkles className="h-3.5 w-3.5" /> },
    { id: "assignments", label: t("candidates.tabs.assignments", "Assignments"), icon: <Users2 className="h-3.5 w-3.5" /> },
    { id: "comments", label: t("candidates.tabs.comments"), icon: <MessageSquare className="h-3.5 w-3.5" /> },
    { id: "scorecard", label: t("candidates.tabs.scorecard", "Scorecard"), icon: <ClipboardList className="h-3.5 w-3.5" /> },
    { id: "documents", label: t("candidates.tabs.documents", "Documents"), icon: <FileText className="h-3.5 w-3.5" /> },
    { id: "tasks", label: t("candidates.tabs.tasks", "Tasks"), icon: <CheckSquare2 className="h-3.5 w-3.5" /> },
    { id: "calendar", label: t("candidates.tabs.calendar", "Calendar"), icon: <Calendar className="h-3.5 w-3.5" /> },
    { id: "mailbox", label: t("candidates.tabs.mailbox", "Mailbox"), icon: <Mail className="h-3.5 w-3.5" /> },
    ...(transcriptsEnabled ? [{ id: "transcripts", label: t("transcripts.title", "Transcripts"), icon: <Video className="h-3.5 w-3.5" /> }] : []),
    { id: "history", label: t("candidates.tabs.history"), icon: <History className="h-3.5 w-3.5" /> },
  ], [t, transcriptsEnabled]);

  const visibleTabConfig = useMemo(() => {
    return tabConfig.filter((tab) => {
      if (tab.id === "potential-matches" && !hasCandidateAiAnalysis)
        return false;
      if (tab.id === "scorecard" && !hasScorecardFields)
        return false;
      return true;
    });
  }, [tabConfig, hasCandidateAiAnalysis, hasScorecardFields]);

  useEffect(() => {
    if (!hasCandidateAiAnalysis && activeTab === "potential-matches")
      setActiveTab("processes");
    if (!hasScorecardFields && activeTab === "scorecard")
      setActiveTab("processes");
  }, [hasCandidateAiAnalysis, hasScorecardFields, activeTab, setActiveTab]);



  //console.log(candidate.ai_analysis)

  return (
    <div className="w-full">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <DynamicTabBar
          tabs={visibleTabConfig}
          pinnedIds={["processes", "experience", "analysis"]}
          dynamicSlotCount={3}
          defaultDynamicIds={["documents", "comments", "assignments"]}
          activeTabId={activeTab}
          onTabChange={setActiveTab}
          entityType="candidate"
          userId={user?.id || ""}
        />
        
        <TabsContent value="processes" className="border border-border p-6 bg-card">
          <h2 className="text-base font-medium text-foreground mb-1">{t("candidates.tabsContent.recruitmentProcess.title")}</h2>
          <p className="text-xs text-muted-foreground mb-4">{t("candidates.tabsContent.recruitmentProcess.description")}</p>
          <div className="overflow-x-auto -mx-6 px-6">
            <CandidateKanban candidateId={candidateId} candidateName={candidate.name} refreshKey={kanbanRefreshKey} />
          </div>
        </TabsContent>

        <TabsContent value="comments" className="border border-border p-6 bg-card">
          <h2 className="text-base font-medium text-foreground mb-1">{t("candidates.tabsContent.comments.title")}</h2>
          <p className="text-xs text-muted-foreground mb-4">{t("candidates.tabsContent.comments.description")}</p>
          <CandidateComments
            candidateId={candidateId}
            comments={comments}
            cv={cv}
            handleAddComment={handleAddComment}
            handleDeleteComment={handleDeleteComment}
            handleUpdateComment={handleUpdateComment}
            isLoading={isLoading}
          />
        </TabsContent>

        <TabsContent value="analysis" className="border border-border p-6 bg-card">
          <h2 className="text-base font-medium text-foreground mb-1">{t("candidates.tabsContent.aiAnalysis.title")}</h2>
          <p className="text-xs text-muted-foreground mb-4">{t("candidates.tabsContent.aiAnalysis.description")}</p>
          <Tabs defaultValue="overall" className="w-full">
            <TabsList className="mb-4">
              <TabsTrigger value="overall">Overall</TabsTrigger>
              <TabsTrigger value="profiles">Profiles</TabsTrigger>
            </TabsList>
            <TabsContent value="overall">
              <CandidateAnalysisTab
                candidateId={candidateId}
                analysis={candidate.ai_analysis || {}}
              />
            </TabsContent>
            <TabsContent value="profiles">
              <CandidateProfilesTab profilesData={candidate.ai_science_analysis || {}} />
            </TabsContent>
          </Tabs>
        </TabsContent>

        <TabsContent value="experience" className="border border-border p-6 bg-card">
          <h2 className="text-base font-medium text-foreground mb-1">{t("candidates.tabsContent.experience.title", "Experience")}</h2>
          <p className="text-xs text-muted-foreground mb-4">{t("candidates.tabsContent.experience.description", "Work experience and professional background.")}</p>
          <CandidateExperienceTab
            candidateId={candidateId}
            aiAnalysis={candidate.ai_analysis || {}}
          />
        </TabsContent>

        {hasCandidateAiAnalysis && (
          <TabsContent value="potential-matches" className="border border-border p-6 bg-card">
            <h2 className="text-base font-medium text-foreground mb-1">{t("candidates.tabsContent.potentialMatches.title", "Potential Matches")}</h2>
            <p className="text-xs text-muted-foreground mb-4">{t("candidates.tabsContent.potentialMatches.description", "View jobs that are a good fit for this candidate.")}</p>
            <CandidatePotentialMatchesTab candidateId={candidateId} candidate={candidate} />
          </TabsContent>
        )}

        <TabsContent value="assignments" className="border border-border p-6 bg-card">
          <CandidateAssignmentsTab
            candidateId={candidateId}
            candidateName={candidate.name}
          />
        </TabsContent>

        <TabsContent value="history" className="border border-border p-6 bg-card">
          <h2 className="text-base font-medium text-foreground mb-1">{t("candidates.tabsContent.history.title")}</h2>
          <p className="text-xs text-muted-foreground mb-4">{t("candidates.tabsContent.history.description")}</p>
          <CandidateHistoryTab candidateId={candidateId} />
        </TabsContent>

        {hasScorecardFields && (
          <TabsContent value="scorecard" className="border border-border p-6 bg-card">
            <h2 className="text-base font-medium text-foreground mb-1">{t("candidates.tabsContent.scorecard.title", "Scorecard")}</h2>
            <p className="text-xs text-muted-foreground mb-4">{t("candidates.tabsContent.scorecard.description", "Evaluate this candidate against your organization's standardized criteria.")}</p>
            <CandidateScorecardTab candidateId={candidateId} />
          </TabsContent>
        )}

        <TabsContent value="documents" className="border border-border p-6 bg-card">
          <h2 className="text-base font-medium text-foreground mb-1">{t("documents.candidateDocumentsTitle", "Candidate Documents")}</h2>
          <p className="text-xs text-muted-foreground mb-4">{t("documents.candidateDocumentsDescription", "All documents linked to this candidate.")}</p>
          <CandidateDocumentsTab candidateId={candidateId} candidate={candidate} />
        </TabsContent>

        <TabsContent value="mailbox" className="border border-border p-6 bg-card">
          <EntityMailbox entityType="candidate" entityId={candidateId} entityName={candidate.name} />
        </TabsContent>

        <TabsContent value="tasks" className="border border-border p-6 bg-card">
          <CandidateTasksTab candidateId={candidateId} candidateName={candidate.name} />
        </TabsContent>

        <TabsContent value="calendar" className="border border-border p-6 bg-card">
          <CandidateCalendarTab candidateId={candidateId} candidateName={candidate.name} />
        </TabsContent>

        <TabsContent value="transcripts">
          <EmbeddedTranscripts candidateId={candidateId} candidateName={candidate.name} />
        </TabsContent>

      </Tabs>
    </div>
  );
};


